import React, { useEffect, useRef } from 'react';

import { NavigationContainerRefWithCurrent } from '@react-navigation/native';

import { getEnv } from '@/data/config';
import { getAppVersion } from '@/utils/get-app-version';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

export const dataDogEnabled = () => !__DEV__;

const initDataDog = (env: string) => {
    datadogRum.init({
        applicationId: '3e872b40-a383-40bb-9a76-551420084421',
        clientToken: 'pubbced38c66a2394e072299c590977909c',
        site: 'datadoghq.com',
        service: 'betr-app-web',
        env: env.toLowerCase(),
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        silentMultipleInit: true,
        version: getAppVersion(),
        sessionSampleRate: 33,
    });
    datadogLogs.init({
        clientToken: 'pubbced38c66a2394e072299c590977909c',
        site: 'datadoghq.com',
        silentMultipleInit: true,
    });
};

export const DataDogAppProvider = ({ children }: { children: React.ReactNode }) => {
    const initialized = useRef(false);
    const ENV = getEnv();

    useEffect(() => {
        if (dataDogEnabled() && !initialized.current) {
            initialized.current = true;
            initDataDog(ENV);
        }
    }, [ENV]);

    return <>{children}</>;
};

// could not find a way to enable navigation tracking in the browser
export const enableDataDogNavTracking = (_nav: NavigationContainerRefWithCurrent<any>) => {};

/**
 * Accumulated extra info that needs to be preserved across setUser calls.
 * setUser replaces the entire user object (including extra properties), so we
 * track extra info locally and re-apply it when the user ID is set.
 */
let cachedExtraInfo: Record<string, string> = {};

export const setDatadogUser = (userId: string) => {
    if (dataDogEnabled()) {
        datadogRum.setUser({ id: userId, ...cachedExtraInfo });
    }
};

export const addDatadogUserExtraInfo = (extraInfo: Record<string, string>) => {
    if (dataDogEnabled()) {
        cachedExtraInfo = { ...cachedExtraInfo, ...extraInfo };
        Object.entries(extraInfo).forEach(([key, value]) => {
            datadogRum.setUserProperty(key, value);
        });
    }
};

export const trackRUMAction = (action: string, args?: object) => {
    if (dataDogEnabled()) {
        datadogRum.addAction(action, args);
    }
};

export const trackRUMView = (view: string) => {
    if (dataDogEnabled()) {
        datadogRum.startView(view);
    }
};

export const DdLogs = {
    debug: ([tag, args]: [string, object]) => {
        datadogLogs.logger.debug(tag, args);
    },
    info: ([tag, args]: [string, object]) => {
        datadogLogs.logger.info(tag, args);
    },
    warn: ([tag, args]: [string, object]) => {
        datadogLogs.logger.warn(tag, args);
    },
    error: ([tag, args]: [string, object]) => {
        console.log('DdLogs.error');
        datadogLogs.logger.error(tag, args);
    },
};
